"use client"

import React, { useMemo, useRef, useState, useTransition } from "react"
import { wait } from "next/dist/lib/wait"
import Link from "next/link"
import { deleteUserAction } from "@/actions/userActions"
import { UserLinkRO, UserLinkROCollection } from "@ub/customer-client"
import type { ResourceAttribute } from "@ub/product-client"
import { EditIcon, LoaderCircle, PlusIcon, SearchIcon, Trash2 } from "lucide-react"

import { Button } from "@/components/ui/components"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/components/alert-dialog"
import { extractUserId, formatCurrency } from "@/components/account/users/users-helper"
import type { UbAccountUsersViewStoryblok } from "@/components/storyblok/component-types"

export function AccountUsersClient(props: {
  blok: UbAccountUsersViewStoryblok
  users?: UserLinkROCollection
  customerNo: string
}) {
  const [searchQuery, setSearchQuery] = React.useState("")
  const inputRef = useRef<HTMLInputElement>(null)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [toBeDeleted, setToBeDeleted] = useState<string | null>(null)
  const [loadingDelete, deleteTransaction] = useTransition()
  const [baseUsers, setBaseUsers] = React.useState<UserLinkRO[]>(props.users?.elements ?? [])

  const userLinks = useMemo(() => {
    if (searchQuery === "") {
      return baseUsers
    }
    const re = RegExp(searchQuery, "i")
    return baseUsers.filter((u) => (u.name ?? "").match(re))
  }, [baseUsers, searchQuery])

  const getAttribute = (attributes: ResourceAttribute[] | undefined, name: string): any => {
    return attributes?.find((attr) => attr.name === name)?.value
  }

  async function handleDelete(login: string) {
    setToBeDeleted(login)
    setConfirmDialogOpen(true)
  }
  async function resetDelete() {
    setConfirmDialogOpen(false)
    setToBeDeleted(null)
  }
  async function handleConfirmedDelete() {
    deleteTransaction(async () => {
      if (toBeDeleted) {
        await deleteUserAction(props.customerNo, toBeDeleted).then(
          () => {
            setBaseUsers(baseUsers.filter((u) => u.login !== toBeDeleted))
          },
          (error) => {
            console.error("Error deleting user:", error)
          }
        )
      }
      await resetDelete()
    })
  }

  return (
    <>
      <div className="flex flex-col gap-4">
        <h2 className="-mb-2 text-xl font-semibold">{props.blok?.label_users_overview}</h2>
        {/* Search / Filter */}
        <div className="rounded-sm border border-gray-300 bg-gray-200">
          <div className="grid grid-rows-1 gap-2 p-4 lg:grid-cols-[auto_auto_max-content]">
            <div className="relative h-fit w-full md:pr-0 lg:max-w-80">
              {searchQuery != "" ? (
                <button
                  onClick={() => {
                    setSearchQuery("")
                    inputRef.current?.focus()
                  }}
                  className={"absolute right-2 top-1/2 size-5 -translate-y-1/2 text-gray-400"}
                >
                  <PlusIcon strokeWidth={2} className={"size-5 rotate-45"} />
                </button>
              ) : (
                <div className={"pointer-events-none absolute right-2 top-1/2 size-5 -translate-y-1/2 text-gray-400"}>
                  <SearchIcon strokeWidth={2} className="size-5" />
                </div>
              )}
              <input
                type="text"
                placeholder={props.blok.label_search_placeholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                ref={inputRef}
                className="w-full rounded-lg border border-gray-300 py-2 pl-4 pr-10 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className={"max-lg:hidden"} />
            <div className={"flex justify-end"}>
              <Button asChild size={"default"} className={"gap-1"}>
                <Link href="/account/users/new">
                  <PlusIcon className={"inline size-5"} />
                  <span>{props.blok?.label_new_user}</span>
                </Link>
              </Button>
            </div>
          </div>
        </div>
        <div>
          <div
            className={
              "grid grid-cols-[auto_auto] gap-2 lg:grid-cols-[max-content_max-content_auto_auto_min-content] xl:gap-x-6"
            }
          >
            {/* Tabellenkopf */}
            <div
              className={
                "col-span-full hidden w-full grid-cols-subgrid grid-rows-1 gap-2 hyphens-auto text-wrap px-2 text-xs font-semibold lg:grid xl:gap-x-6 xl:text-sm"
              }
            >
              <span>{props.blok?.label_headline_user}</span>
              <span>{props.blok?.label_headline_budget}</span>
              <span>{props.blok?.label_headline_open_orders}</span>
              <span>{props.blok?.label_headline_roles}</span>
              <span></span>
            </div>

            {/* Benutzerzeilen */}
            {userLinks?.map((userLink) => {
              const orderSpentLimit = getAttribute(userLink.attributes, "orderSpentLimit")
              const budget = getAttribute(userLink.attributes, "budget")
              const remainingBudget = getAttribute(userLink.attributes, "remainingBudget")
              const budgetPeriod = getAttribute(userLink.attributes, "budgetPeriod")
              const roleNames = getAttribute(userLink.attributes, "roleNames") || []
              const pendingOnce = getAttribute(userLink.attributes, "pendingOneTimeRequisitionsCount")
                ? (getAttribute(userLink.attributes, "pendingOneTimeRequisitionsCount") as number)
                : 0
              const pendingRecurring = getAttribute(userLink.attributes, "pendingRecurringRequisitionsCount") || 0
              const totalPending = pendingOnce + pendingRecurring

              const budgetUsedPercent =
                budget && remainingBudget && budget.value > 0
                  ? ((budget.value - remainingBudget.value) / budget.value) * 100
                  : 0

              return (
                <div
                  key={userLink.login}
                  className="relative col-span-full grid w-full grid-cols-subgrid gap-2 rounded-sm border border-gray-200 bg-white p-2 xl:gap-x-6"
                >
                  {/* Benutzer */}
                  <div className="max-lg:col-span-full max-lg:min-h-6 max-lg:pr-14 lg:order-1">
                    {userLink.login && (
                      <Link
                        href={`/account/users/${encodeURIComponent(extractUserId(userLink))}`}
                        className="font-semibold text-gray-800 hover:underline"
                      >
                        {userLink.name}
                      </Link>
                    )}
                  </div>
                  <div className="flex gap-2 max-lg:absolute max-lg:right-2 max-lg:top-2 lg:order-5 lg:flex-col">
                    {userLink.login && (
                      <>
                        <Link
                          href={`/account/users/${encodeURIComponent(extractUserId(userLink))}`}
                          className={"hover:text-primary-hover"}
                        >
                          <EditIcon className={"inline size-5"} />
                        </Link>

                        <button
                          className={"hover:text-primary-hover disabled:text-gray-400"}
                          onClick={() => handleDelete(userLink.login ?? "")}
                        >
                          <Trash2 className={"inline size-5"} />
                        </button>
                      </>
                    )}
                  </div>

                  {/* Budget */}
                  <div className="space-y-2 text-sm max-lg:row-span-2 lg:order-2">
                    <div>
                      <p className="font-light">{props.blok?.label_limit_per_order}</p>
                      <p>{orderSpentLimit ? formatCurrency(orderSpentLimit) : props.blok?.label_unlimited}</p>
                    </div>
                    <div>
                      {budget && budgetPeriod !== "none" ? (
                        <>
                          <p className="font-light">
                            {props.blok?.label_budget} (
                            {budgetPeriod === "weekly"
                              ? (props.blok?.label_weekly ?? "")
                              : budgetPeriod === "monthly"
                                ? (props.blok?.label_monthly ?? "")
                                : budgetPeriod === "quarterly"
                                  ? (props.blok?.label_quarterly ?? "")
                                  : budgetPeriod === "yearly"
                                    ? (props.blok?.label_yearly ?? "")
                                    : budgetPeriod}
                            )
                          </p>
                          <p>{formatCurrency(budget)}</p>
                          <div className="mt-1 w-24 rounded border bg-gray-100 p-1 text-center text-xs">
                            {budgetUsedPercent.toFixed(1).replace(".", ",")}%
                          </div>
                        </>
                      ) : (
                        <p className="text-gray-500">{props.blok?.label_no_budget_defined}</p>
                      )}
                    </div>
                  </div>

                  {/* Rollen & Aktionen */}
                  <div className="text-sm lg:order-4">
                    <p className={"font-light lg:hidden"}>{props.blok?.label_headline_roles}</p>
                    {roleNames.map((role: string) => (
                      <div key={role}>{role}</div>
                    ))}
                  </div>

                  {/* Offene Bestellungen */}
                  <div className="text-sm lg:order-3 lg:text-right">
                    <p className={"font-light lg:hidden"}>{props.blok?.label_headline_open_orders}</p>
                    {(props.blok?.label_number_of_orders ?? "").replaceAll("%1", totalPending)}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen} defaultOpen={false}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{props.blok.label_alert_title}</AlertDialogTitle>
            <AlertDialogDescription>{props.blok.label_alert_message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={resetDelete} disabled={loadingDelete}>
              {props.blok.label_common_cancel}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmedDelete} disabled={loadingDelete}>
              {loadingDelete ? (
                <LoaderCircle className={"motion-safe:animate-spin"} />
              ) : (
                props.blok.label_common_confirm
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
